/**
 * Types and static rule tables ported from the real journey_mapper.py
 * (data-continuiti). See engine/journey-mapper-utils.ts for the alignment
 * logic that consumes these.
 */

export interface DetectedTool {
  name: string;
  category: string;
  cap: string;
}

export const MilestoneStatus = {
  COVERED: 'covered',
  GAP: 'gap',
  DISCOVERY: 'discovery'
} as const;
export type MilestoneStatusType = (typeof MilestoneStatus)[keyof typeof MilestoneStatus];

export interface JourneyMilestoneResult {
  no: number;
  stage: number;
  name: string;
  purpose: string;
  signal: string;
  notes: string;
  requires: string[];
  status: MilestoneStatusType;
  tools: string[];
}

export interface JourneyStageResult {
  num: number;
  name: string;
  milestones: JourneyMilestoneResult[];
  covered: number;
  detectable: number;
  discovery: number;
}

export interface FoundationItem {
  cap: string;
  label: string;
  group: string;
  present: boolean;
  tools: string[];
}

export interface JourneySuggestion {
  name: string;
  stage: number;
  stageName: string;
  section: string;
  signal: string;
  purpose: string;
  reason: string;
  more: string;
}

export interface JourneyRecommendation {
  severity: 'GAP';
  stage: string;
  milestone: string;
  title: string;
  note: string;
  more: string;
}

export interface FoundationObservation {
  severity: 'RISK' | 'GAP' | 'OPPORTUNITY';
  title: string;
  note: string;
  more: string;
}

export interface RedundancyFinding {
  cap: string;
  label: string;
  tools: string[];
  note: string;
  more: string;
}

export interface JourneyLibraryEntry {
  section: string;
  stage: number;
  name: string;
  signal: string;
  purpose: string;
}

export interface JourneyModel {
  clientName: string;
  domain: string;
  clientType: string;
  clientTypeLabel: string;
  vertical: string;
  verticalLabel: string;
  templateUsed: string;
  stages: JourneyStageResult[];
  foundation: FoundationItem[];
  foundationGroups: Record<string, string>;
  tools: DetectedTool[];
  suggestions: JourneySuggestion[];
  recommendations: JourneyRecommendation[];
  foundationObservations: FoundationObservation[];
  redundancies: RedundancyFinding[];
  score: { covered: number; detectable: number; pct: number };
  capabilities: Record<string, string>;
  library: JourneyLibraryEntry[];
}

/** An in-progress edit to a journey's milestones (user-customized in the UI). */
export interface EditableMilestone {
  stage: number;
  name: string;
  signal?: string;
  purpose?: string;
  notes?: string;
}

export const TYPE_TO_JT: Record<string, string> = {
  ecomm: 'JT eComm',
  leadgen: 'JT Lead Gen',
  ecomm_sub: 'JT eComm Sub',
  ecomm_store: 'JT eComm Store',
  hybrid: 'JT Hybrid'
};

/** How detected platforms are described in journey terms. Foundation capabilities support every stage. */
export const CAPABILITIES: Record<string, { label: string; foundation: boolean }> = {
  paid_media: { label: 'Paid media & pixels', foundation: false },
  affiliate: { label: 'Affiliate & partnerships', foundation: false },
  lifecycle: { label: 'Email / SMS lifecycle', foundation: false },
  testing_perso: { label: 'Testing & personalization', foundation: false },
  social_proof: { label: 'Reviews & social proof', foundation: false },
  loyalty: { label: 'Loyalty & referral', foundation: false },
  chat: { label: 'Chat & engagement', foundation: false },
  commerce: { label: 'Commerce platform', foundation: false },
  crm: { label: 'CRM / sales platform', foundation: false },
  call_tracking: { label: 'Call tracking & intelligence', foundation: false },
  site_search: { label: 'Site search & merchandising', foundation: false },
  payments: { label: 'Payments & checkout', foundation: false },
  subscriptions: { label: 'Subscription management', foundation: false },
  post_purchase: { label: 'Post-purchase experience', foundation: false },
  scheduling: { label: 'Scheduling & booking', foundation: false },
  voc: { label: 'Surveys, quizzes & VoC', foundation: false },
  attribution: { label: 'Attribution & measurement', foundation: true },
  identity: { label: '1P data & identity signals', foundation: true },
  analytics: { label: 'Analytics', foundation: true },
  ux_analytics: { label: 'Session replay / UX', foundation: true },
  tag_mgmt: { label: 'Tag management', foundation: true },
  sst: { label: 'Server-side tagging', foundation: true },
  cdp: { label: 'CDP / data platform', foundation: true },
  consent: { label: 'Consent management (CMP)', foundation: true },
  consent_mode: { label: 'Consent signals (Consent Mode / TCF / GPP)', foundation: true }
};

export const FOUNDATION_CAPS = Object.entries(CAPABILITIES)
  .filter(([, v]) => v.foundation)
  .map(([k]) => k);

/** Two foundations: the website's measurement plumbing vs. the media-side data backbone. */
export const FOUNDATION_GROUPS: Record<string, { label: string; caps: string[] }> = {
  site: {
    label: 'Website & infrastructure foundation',
    caps: ['analytics', 'tag_mgmt', 'ux_analytics', 'consent']
  },
  media: {
    label: 'Media foundation',
    caps: ['pixels', 'consent_mode', 'sst', 'cdp', 'attribution', 'identity']
  }
};
export const PIXELS_LABEL = 'Conversion pixels';

export const ECOM_PLATFORMS =
  /shopify|woocommerce|magento|adobe commerce|bigcommerce|salesforce commerce|sfcc/i;

/** Scanner detection category -> capability. Keyed to the real signature categories in martech_scanner.py. */
export const CATEGORY_RULES: Record<string, string | null> = {
  'Advertising & Pixels': 'paid_media',
  'DSP / Programmatic': 'paid_media',
  'CRM / Sales & ABM': 'crm',
  'Call Tracking & Intelligence': 'call_tracking',
  'Site Search & Merchandising': 'site_search',
  'Payments & Checkout': 'payments',
  'Subscription Management': 'subscriptions',
  'Attribution & Measurement': 'attribution',
  'Identity & First-Party Data': 'identity',
  'Post-Purchase Experience': 'post_purchase',
  'Scheduling & Booking': 'scheduling',
  'Surveys, Quizzes & VoC': 'voc',
  'Runtime-Confirmed Pixel Fires': null, // duplicate view of pixels
  'Affiliate / Partnership': 'affiliate',
  'Email / SMS / Marketing Automation': 'lifecycle',
  'A/B Testing & Personalization': 'testing_perso',
  'Reviews / UGC / Loyalty': 'social_proof',
  'Chat / Support / Engagement': 'chat',
  Analytics: 'analytics',
  'Session Replay / UX Analytics': 'ux_analytics',
  'Tag Management': 'tag_mgmt',
  'Server-Side Tagging Signals': 'sst',
  'CDP / Data Platforms': 'cdp',
  'Consent Management': 'consent',
  'Platform / CMS': 'commerce', // ecom platforms only — see classifyTool
  'Frontend Framework': null,
  'Headless CMS / Content API': null,
  'Performance / Infrastructure': null
};

/** Tool-name overrides (substring, case-insensitive): pattern -> capability or null. */
export const TOOL_RULES: [RegExp, string | null][] = [
  [/google consent mode|iab tcf|us privacy|\bgpp\b/i, 'consent_mode'],
  [/smile\.io|loyaltylion/i, 'loyalty'],
  [/launchdarkly/i, null], // feature flags, not marketing perso
  [/google optimize/i, null] // sunset — noise
];

/** Platform variants that are one system for journey purposes (headless vs. standard still matters for the stack report). */
export const CANONICAL_RULES: [string, RegExp, string][] = [
  ['Platform / CMS', /shopify/i, 'Shopify'],
  ['Platform / CMS', /magento|adobe commerce/i, 'Magento / Adobe Commerce'],
  ['Platform / CMS', /salesforce commerce|sfcc/i, 'Salesforce Commerce Cloud']
];

/**
 * Milestone name/signal -> detectable capability requirements. Matched
 * against "name | signal"; FIRST match wins, so specific rules sit above
 * generic ones. requires = any-of capability list; [] = discovery (not
 * observable client-side). Unmatched milestones default to discovery.
 * soft=true: covered when the capability is detected, but its ABSENCE falls
 * back to discovery, never gap — for systems (CRM) that routinely have zero
 * client-side footprint.
 */
export const MILESTONE_RULES: [RegExp, string[], boolean][] = [
  // not scan-detectable — offline, calls, app, in-store, regulated
  [/call center/i, [], false],
  [/call from|click.to.call|dni|dynamic number|phone/i, ['call_tracking'], false],
  [/bnpl|financing|hsa|fsa|gift card/i, ['payments'], false],
  [
    /appointment|consultation scheduled|class booked|table booked|demo scheduled/i,
    ['scheduling', 'crm'],
    true
  ],
  [/site search|search & filter|merchandis|product finder/i, ['site_search', 'analytics'], false],
  [/survey|nps\b|voice of customer|\bquiz\b/i, ['voc', 'analytics'], false],
  [
    /\breturns?\b.{0,12}(started|completed|portal)|\brma\b|order tracking|track(ing)? page/i,
    ['post_purchase', 'lifecycle'],
    false
  ],
  [
    /crm|closed.won|consultation|quote delivered|contract signed|job completed|in.home|dealer|pipeline|opportunity|qualified lead|sales accepted/i,
    ['crm'],
    true
  ],
  [
    /offline|in.store|store visit|\bpos\b|card link|bopis|pickup|showroom|trade.in|consignment|authentication/i,
    [],
    false
  ],
  [
    /app install|app store|push|deep.link|in.app|mmp|store page|app engagement|adherence|refill|web.to.app/i,
    [],
    false
  ],
  [
    /kyc|first deposit|wager|first trade|insurance|eligibility|prescription|provider|intake|\brx\b/i,
    [],
    false
  ],
  // detectable
  [/paid media|ad impression|branded search|qr code|promo code|retarget/i, ['paid_media'], false],
  [
    /email\s*\/?\s*sms|sms capture|signup form|welcome|winback|dunning|onboarding|first delivery|fulfillment|shipment|delivery confirmed|stewardship|nurture|drip/i,
    ['lifecycle', 'post_purchase'],
    false
  ],
  [/review|ugc/i, ['social_proof'], false],
  [/loyalty|points|tier|membership/i, ['loyalty'], false],
  [/referral|advocacy|ambassador|peer.to.peer|affiliate/i, ['loyalty', 'affiliate'], false],
  [/chat/i, ['chat'], false],
  [
    /cross.sell|upsell|upgrade|save offer|pause|reorder|replenish/i,
    ['lifecycle', 'commerce', 'subscriptions'],
    false
  ],
  [
    /add.to.cart|checkout|purchase|view_item|product detail|transaction|pre.order|subscription start|subscription_start|rebill|recurring order|recurring_purchase|plan selected|one.time|first order|booking confirmed|ticket/i,
    ['commerce', 'subscriptions'],
    false
  ],
  [/personaliz|a\/b|variant|recommendation/i, ['testing_perso'], false],
  // site-native behaviors measured through analytics events
  [
    /visit|session|engagement|page|content|browse|search|quiz|assessment|configurator|calculator|estimate|gift finder|path identified|lead form|generate_lead|form submit|form start|account created|sign.up|age gate|donation|petition|waitlist|reminder|registry|comparison|wishlist|save/i,
    ['analytics'],
    false
  ]
];

/**
 * Evidence text -> suggested Non-standard Milestone Library entries (unless
 * already in the client's journey). Milestone names must match the library
 * exactly.
 */
export const SUGGESTION_RULES: [RegExp, string[], string][] = [
  [
    /affirm|klarna|afterpay|sezzle|zip\.co|splitit/i,
    ['BNPL Selection at Checkout'],
    'BNPL provider on the page ({m})'
  ],
  [
    /synchrony|bread(?:payments|financial)?\.com|acima|koalafi|wellsfargo.*retail/i,
    ['Financing Pre-Qualification (Soft Pull)'],
    'Store-financing provider detected ({m})'
  ],
  [
    /truemed|sika(?:health)?|flexpay.*hsa|binkey/i,
    ['HSA / FSA Payment Used'],
    'HSA/FSA payment enabler detected ({m})'
  ],
  [
    /rise\.ai|giftup|govalo|gift.?card/i,
    ['Gift Card Purchase / Redemption', 'Gift Card / e-Gift Sent'],
    'Gift-card platform detected ({m})'
  ],
  [
    /appsflyer|adjust\.(?:com|io)|branch\.io|impact\.branch|kochava|singular\.net/i,
    ['App Store Page View', 'App Install & First Open', 'First In-App Purchase / Order'],
    'Mobile measurement partner detected ({m}) — an app is part of this journey'
  ],
  [
    /onesignal|airship|urbanairship|pushowl|wonderpush|smartbanner|tapcart/i,
    ['Push / Notification Opt-In', 'Web-to-App Handoff'],
    'Push/app-engagement SDK detected ({m})'
  ],
  [
    /recharge(?:payments|apps)?\.com|ordergroove|smartrr|skio|stay\.ai|bold(?:commerce)?\.com|awtomic|loop.*subscription/i,
    [
      'Failed Payment Recovered (Dunning)',
      'Plan Pause (Save Offer Accepted)',
      'Annual / Prepaid Upgrade'
    ],
    'Subscription manager detected ({m}) — retention-mechanics milestones apply'
  ],
  [
    /smile\.io|loyaltylion|yotpo.*loyalty|annex.?cloud|zinrelo|antavo/i,
    ['Points Redemption', 'Tier Upgrade / VIP Status'],
    'Loyalty platform detected ({m}) — program-health milestones apply'
  ],
  [
    /okendo|yotpo|bazaarvoice|stamped|powerreviews|loox|junip/i,
    ['UGC Submission / Contest Entry'],
    'Reviews/UGC platform detected ({m})'
  ],
  [
    /extend\.com|helloclyde|getmulberry|route\.com.*protect/i,
    ['Extended Warranty / Protection Attach'],
    'Protection-plan provider detected ({m})'
  ],
  [
    /6sense|demandbase|terminus|rollworks|clearbit|zoominfo|bombora|linkedin insight/i,
    ['Target Account Engagement (ABM)'],
    'ABM/intent platform detected ({m})'
  ],
  [
    /navattic|storylane|walnut\.io|reprise|arcade\.software/i,
    ['Interactive Demo / Product Tour'],
    'Interactive-demo platform detected ({m})'
  ],
  [
    /agechecker|bluecheck|veratad|ageverif|yoti/i,
    ['Age Gate Passed'],
    'Age-verification provider detected ({m})'
  ],
  [
    /withpersona|persona\.com|jumio|onfido|alloy\.co|socure|veriff|plaid/i,
    ['KYC / Identity Verification Completed', 'First Deposit Funded'],
    'Identity-verification provider detected ({m})'
  ],
  [
    /threekit|zakeke|cylindo|configur/i,
    ['Product Configurator Completed', 'Saved Build / Project Retrieved'],
    'Product-configurator platform detected ({m})'
  ],
  [
    /8thwall|vertebrae|ar-?quicklook|levar|aryel/i,
    ['Virtual / AR Try-On or Room Placement'],
    'AR try-on/visualization tech detected ({m})'
  ],
  [
    /eventbrite|on24|goldcast|bizzabo|splashthat|livestorm|hopin|zoom.*webinar|hubilo/i,
    ['Webinar / Class Registration'],
    'Event/webinar platform detected ({m})'
  ],
  [
    /discord|circle\.so|insided|khoros|higherlogic|vanilla.?forums|bettermode/i,
    ['Community / Forum Registration'],
    'Community platform detected ({m})'
  ],
  [
    /tiktok.?shop|instagram.?shop|meta.?shops|shop\.app|whatnot|commentsold|channelize|bambuser|firework\.tv/i,
    ['Social Checkout (TikTok Shop / IG)', 'Live Shopping Event Attended'],
    'Social/live commerce surface detected ({m})'
  ],
  [
    /back.?in.?stock|restock.*alert|klaviyo.*bis|waitlist/i,
    ['Waitlist / Back-in-Stock Signup'],
    'Back-in-stock/waitlist mechanics detected ({m})'
  ],
  [
    /registrylove|myregistry|babylist|zola/i,
    ['Registry Created (Wedding / Baby)'],
    'Registry platform detected ({m})'
  ],
  [
    /friendbuy|extole|mention.?me|talkable|referralcandy/i,
    ['Recipient Converted to Customer'],
    'Referral platform detected ({m}) — measure referred-recipient conversion'
  ],
  [
    /stackla|tintup|pixlee|olapic|emplifi/i,
    ['UGC Submission / Contest Entry', 'Ambassador / Creator Application'],
    'UGC/creator platform detected ({m})'
  ]
];

/** Revenue-nearest stage order for prioritizing gaps: Conversion, Repeat, Consideration, Post-Sale, Awareness. */
export const STAGE_PRIORITY: Record<number, number> = { 3: 0, 5: 1, 2: 2, 4: 3, 1: 4 };

/** "Why this matters" copy for expandable detail on gap recommendations. */
export const CAP_MORE: Record<string, string> = {
  paid_media:
    "Without pixels supporting this milestone, paid platforms can't attribute it, bid toward it, or build audiences from it — spend upstream of this point is flying blind, and retargeting pools never form.",
  lifecycle:
    'Email/SMS is the channel where reaching the user again costs nearly nothing. A milestone with no lifecycle platform behind it means abandonment, onboarding, and winback moments pass with no owned-channel touch — the journey depends entirely on paid re-acquisition.',
  social_proof:
    'Buyers look for third-party validation at exactly this point. Without a reviews/UGC platform the brand has no structured way to collect proof, surface it on PDPs and ads, or feed ratings into shopping feeds where they influence click-through.',
  loyalty:
    "Repeat behavior that isn't systematically rewarded regresses to the mean. A loyalty/referral platform turns the best customers into a measurable retention and acquisition asset instead of a lucky accident.",
  chat: "High-intent visitors with a blocking question at this milestone either get an answer in the moment or leave. Chat also captures contact info and routes complex needs — without it, those conversations happen on a competitor's site.",
  commerce:
    'No commerce platform signature at a transactional milestone usually means checkout happens off-site or on an undetected system — either way, verify that conversion tracking actually reaches the point of purchase before trusting any downstream numbers.',
  testing_perso:
    'Every visitor sees the same experience at this milestone. A testing/personalization tool is how conversion-rate problems become experiments instead of opinions.',
  analytics:
    "This milestone is measured through site analytics events; with no analytics platform detected, it isn't being captured at all — no funnel visibility, no audiences, no optimization signal.",
  site_search:
    'Visitors who use site search convert at multiples of those who browse — a dedicated search/merchandising platform turns that intent into results pages that actually sell.',
  payments:
    'Friction at the payment step is the most expensive friction there is. Flexible payment options (BNPL, wallets) measurably lift conversion and average order value at this exact moment.',
  subscriptions:
    'Recurring revenue lives or dies on the mechanics — dunning, pauses, swaps, upgrades. Without a subscription platform those moments are unmanaged and churn goes unfought.',
  post_purchase:
    'The order-tracking page is the most-visited page a customer sees after buying — owning that experience (and returns) turns anxiety into a branded touchpoint and a second-sale surface.',
  scheduling:
    'When the conversion is an appointment, the scheduler IS the checkout. A booking platform removes the back-and-forth that loses ready-to-buy customers.',
  voc: "Analytics shows what happened; surveys show why. Post-purchase attribution surveys in particular recover 'how did you hear about us' truth that pixels can no longer see.",
  call_tracking:
    'For businesses where the sale happens on the phone, untracked calls are untracked revenue. Dynamic number insertion ties each call to the ad, keyword, or page that drove it — without it, the highest-intent conversions are invisible to every ad platform.',
  crm: 'Pipeline milestones are only measurable when CRM data closes the loop back to marketing. Without a connected CRM, everything after the lead form is invisible — no offline conversion uploads, no closed-won attribution, no audience suppression from the sales pipeline.',
  affiliate:
    'Partner and referral traffic without a management platform is untracked, unattributed, and unpaid-for correctly — the channel exists but nobody can see or grow it.'
};

export const FOUNDATION_MORE: Record<string, string> = {
  consent:
    "Ad platforms increasingly require consent signals (e.g. Google's EEA enforcement), and US state privacy laws keep expanding. Retrofitting consent later forces tag rework across every platform at once — building it in before media scales is dramatically cheaper than after.",
  cdp: 'Without stitched profiles, the user who clicked the ad, the one who opened the email, and the one who redeemed loyalty points look like three different people. Suppression, retargeting efficiency, and lifecycle personalization all degrade when identity is fragmented across platforms.',
  sst: 'Browser-side tracking keeps losing signal to ITP, ad blockers, and consent declines. Server-side collection recovers match quality on conversion events, which directly improves the bidding algorithms every paid channel runs on.'
};

export const CONSENT_CRITICAL = new Set(['health', 'finance']);
